import {
  buildComplianceNavGroups,
  buildDocumentNavGroups,
  buildGeneralSystemNavGroups,
  buildPlatformNavGroups,
  buildRiskAnalysisNavGroups,
  buildUnifiedPersonalDataNavGroups,
  mapReferenceGroupsToSidebarItems,
} from "../lib/definitions/reference-nav-items.js";
import { bigdesAssetGroupCriticalityTabsConfig } from "./bigdesAssetGroupCriticalityTabsConfig.js";
import { bigdesAuditTabsConfig } from "./bigdesAuditTabsConfig.js";
import { bigdesDefinitionsTabsConfig } from "./bigdesDefinitionsTabsConfig.js";

const allowAll = () => true;

const DEFINITION_HUBS = [
  {
    id: "organization",
    build: ({ tBreadcrumb, tNav }) => buildGeneralSystemNavGroups(tBreadcrumb, tNav, allowAll),
    activePathPrefixes: ["/definitions/organization"],
  },
  {
    id: "scale",
    build: ({ tMatrix, tNav }) => buildRiskAnalysisNavGroups(tMatrix, tNav, allowAll),
    activePathPrefixes: ["/definitions/scale"],
  },
  {
    id: "personal-data",
    build: ({ tNav }) => buildUnifiedPersonalDataNavGroups(tNav, allowAll),
    activePathPrefixes: [
      "/definitions/kvkk",
      "/definitions/obtaining-explicit-consent",
      "/definitions/application-tracking",
      "/definitions/privacy",
    ],
  },
  {
    id: "compliance-policy",
    build: ({ tNav }) => buildComplianceNavGroups(tNav, allowAll),
    activePathPrefixes: ["/definitions/compliance"],
  },
  {
    id: "document",
    build: ({ tBreadcrumb, tNav }) => buildDocumentNavGroups(tBreadcrumb, tNav, allowAll),
    activePathPrefixes: ["/definitions/document"],
  },
  {
    id: "platform",
    build: ({ tNav }) => buildPlatformNavGroups(tNav, allowAll),
    activePathPrefixes: [
      "/definitions/integrations",
      "/definitions/automations",
      "/definitions/surveys",
      "/definitions/survey-responses",
    ],
  },
];

export const DEFINITION_HUB_IDS = new Set(DEFINITION_HUBS.map((hub) => hub.id));

const HEADER_TAB_HUBS = [
  {
    parentId: "bigdes-operations",
    id: "definitions",
    tabsConfig: bigdesDefinitionsTabsConfig,
    activePathPrefixes: ["/bigdes-operations/definitions"],
    dataHelper: "bigdes-definitions-tabs",
    labels: {
      "circular-article-definitions": {
        key: "nav.bigdesOperations.circularArticleDefinitions",
        defaultValue: "Circular Article Definitions",
      },
      measures: {
        key: "nav.bigdesOperations.measures",
        defaultValue: "Measures",
      },
    },
    leafHelpers: {},
  },
  {
    parentId: "bigdes-operations",
    id: "asset-group-criticality",
    tabsConfig: bigdesAssetGroupCriticalityTabsConfig,
    activePathPrefixes: ["/bigdes-operations/asset-group-criticality-degree-determination"],
    dataHelper: "bigdes-criticality-tabs",
    labels: {
      "asset-group-evaluation": {
        key: "nav.bigdesOperations.assetGroupEvaluation",
        defaultValue: "Asset Group Evaluation",
      },
      "survey-filling": {
        key: "nav.bigdesOperations.surveyFilling",
        defaultValue: "Survey Filling",
      },
    },
    leafHelpers: {
      "survey-filling": "bigdes-survey-tabs",
    },
  },
  {
    parentId: "bigdes-operations",
    id: "audit",
    tabsConfig: bigdesAuditTabsConfig,
    activePathPrefixes: ["/bigdes-operations/audit"],
    dataHelper: "bigdes-audit-tabs",
    labels: {
      "application-process-evaluation": {
        key: "nav.bigdesOperations.applicationProcessEvaluation",
        defaultValue: "Implementation Process Evaluation",
      },
      "current-situation-evaluation": {
        key: "nav.bigdesOperations.currentSituationEvaluation",
        defaultValue: "Current Situation Evaluation",
      },
      "audit-reports": {
        key: "nav.bigdesOperations.auditReports",
        defaultValue: "Audit Reports",
      },
    },
    leafHelpers: {
      "application-process-evaluation": "bigdes-process-tabs",
      "audit-reports": "bigdes-auditreports-tabs",
    },
  },
];

export const HEADER_TAB_HUB_IDS = new Set(HEADER_TAB_HUBS.map((hub) => hub.id));

function identityTranslate(key, options) {
  return options?.defaultValue || key;
}

/**
 * Promote definition hub landing links into collapsible parents whose leaves
 * come from the existing reference-nav catalogs (permission-filtered later).
 * @param {{ navMain?: Array<Record<string, unknown>> }} navData
 * @param {{ tNav?: Function, tBreadcrumb?: Function, tMatrix?: Function, t?: Function }} [translators]
 */
export function attachDefinitionHubChildren(navData, translators = {}) {
  const tNav = translators.tNav || translators.t || identityTranslate;
  const tBreadcrumb = translators.tBreadcrumb || tNav;
  const tMatrix = translators.tMatrix || tNav;
  const ctx = { tNav, tBreadcrumb, tMatrix };

  return {
    ...navData,
    navMain: (navData.navMain || []).map((item) => {
      if (item.id !== "definitions" || !Array.isArray(item.items)) return item;
      return {
        ...item,
        items: item.items.map((sub) => {
          const hub = DEFINITION_HUBS.find((entry) => entry.id === sub.id);
          if (!hub) return sub;
          return {
            ...sub,
            url: "#",
            activePathPrefixes: hub.activePathPrefixes,
            items: mapReferenceGroupsToSidebarItems(hub.build(ctx)),
          };
        }),
      };
    }),
  };
}

/**
 * Promote HeaderTabs landing links (BIGDES definitions / criticality / audit)
 * into collapsible parents whose leaves come from the tab configs.
 * @param {{ navMain?: Array<Record<string, unknown>> }} navData
 * @param {(key: string, options?: object) => string} t
 */
export function attachHeaderTabHubChildren(navData, t = identityTranslate) {
  const hubsByParent = new Map();
  for (const hub of HEADER_TAB_HUBS) {
    const list = hubsByParent.get(hub.parentId) || [];
    list.push(hub);
    hubsByParent.set(hub.parentId, list);
  }

  return {
    ...navData,
    navMain: (navData.navMain || []).map((item) => {
      const hubs = hubsByParent.get(item.id);
      if (!hubs || !Array.isArray(item.items)) return item;
      return {
        ...item,
        items: item.items.map((sub) => {
          const hub = hubs.find((entry) => entry.id === sub.id);
          if (!hub) return sub;
          return {
            ...sub,
            url: "#",
            activePathPrefixes: hub.activePathPrefixes,
            dataHelper: hub.dataHelper,
            items: (hub.tabsConfig.tabs || []).map((tab) => {
              const label = hub.labels[tab.id] || { key: tab.id, defaultValue: tab.id };
              return {
                id: tab.id,
                title: t(label.key, { defaultValue: label.defaultValue }),
                url: tab.path,
                pageKey: tab.pageKey,
                dataHelper: hub.leafHelpers?.[tab.id],
              };
            }),
          };
        }),
      };
    }),
  };
}

/**
 * Drop hub leaves the user cannot View, then drop empty hub parents.
 * @param {Array<Record<string, unknown>> | undefined} items
 * @param {(pageKey: string) => boolean} canView
 * @param {(path: string) => string | null | undefined} getPageKeyByPath
 */
export function pruneDefinitionHubNavItems(items, canView, getPageKeyByPath) {
  return (items || [])
    .map((item) => {
      if (Array.isArray(item.items) && item.items.length > 0) {
        const children = pruneDefinitionHubNavItems(item.items, canView, getPageKeyByPath);
        if (children.length === 0) return null;
        return { ...item, items: children };
      }
      const pageKey = item.pageKey
        || item.permissionKey
        || (item.url && item.url !== "#" ? getPageKeyByPath(item.url) : null);
      if (pageKey && !canView(pageKey)) return null;
      return item;
    })
    .filter(Boolean);
}
